import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { lifecycle } from 'recompose'
import * as R from 'ramda'

import VoltageRegulator from 'components/form/notebookVoltageRegulator/index'
import { createNamedReducer, getState } from 'helpers/ReducerUtil'
import { createNamedAction } from 'helpers/ActionUtil'
import { voltageValueReducer, createUiReducer } from './reducer'
import injectReducer from 'helpers/injectReducer'
import withSagas from './sagas'
import getSelectors from './selectors'
import {
  togglePopup,
  getUiActions,
  updateConfigSelection,
  updateConfigSource,
  addConfig,
  deletePart,
  updateCurConfig,
  updatePhaseCount,
  updateEfficiency,
  selectFilter,
  deleteFilter,
  updateEdpPeak,
  updateEdpContinuous,
  updateOutputVoltage,
  resetFilter,
  runRiskValidation as runRiskValidationAction,
  isEditPart,
  isAddPart,
  editConfig
} from './actions'
import {
  addPowerComponents,
  updatePowerComponents,
  removePowerComponents
} from '../PowerComponents/actions'
import getPowerComponentSelectors from '../PowerComponents/selectors'
import { elementSelector } from 'containers/Form/selectors'
import {
  getPath,
  getUiPath,
  combinePath
} from 'containers/Form/util'

const withStateReducer = (path, id) => {
  const valuePath = getPath(path, id)
  const valueReducer = createNamedReducer(valuePath, voltageValueReducer)
  return injectReducer(valuePath, valueReducer)
}

const withUiReducer = (path, id) => {
  const uiPath = getUiPath(id)
  const uiReducer = createUiReducer(uiPath)
  return injectReducer(uiPath, uiReducer)
}

const withConnect = (path, id) => {
  const combinedPath = combinePath(path, id)
  const valuePath = getPath(path, id)
  const uiPath = getUiPath(id)

  // Share actions and reducer with power components if power components form element is specified
  const formElements = elementSelector(getState())
  const powerComponentElement = Object.keys(formElements).filter(id => formElements[id].key === 'power_components')
  const powerComponentId = powerComponentElement.length > 0 ? powerComponentElement[0] : ''
  const sharedPath = getPath(path, powerComponentId)
  const combinedSharedPath = combinePath(path, powerComponentId)

  const addConfigAction = createNamedAction(valuePath, addConfig)
  const deletePartAction = createNamedAction(valuePath, deletePart)
  const updatePhaseCountAction = createNamedAction(valuePath, updatePhaseCount)
  const updateEfficiencyAction = createNamedAction(valuePath, updateEfficiency)
  const updateEdpPeakAction = createNamedAction(valuePath, updateEdpPeak)
  const updateEdpContinuousAction = createNamedAction(valuePath, updateEdpContinuous)
  const updateOutputVoltageAction = createNamedAction(valuePath, updateOutputVoltage)
  const setEditPart = createNamedAction(valuePath, isEditPart)
  const setIsAddPart = createNamedAction(valuePath, isAddPart)
  const editConfigAction = createNamedAction(valuePath, editConfig)

  const togglePopupAction = createNamedAction(uiPath, togglePopup)
  const updateConfigAction = createNamedAction(uiPath, updateConfigSelection)
  const updateConfigSourceAction = createNamedAction(uiPath, updateConfigSource)
  const updateCurConfigAction = createNamedAction(uiPath, updateCurConfig)
  const selectFilterAction = createNamedAction(uiPath, selectFilter)
  const deleteFilterAction = createNamedAction(uiPath, deleteFilter)
  const resetFilterAction = createNamedAction(uiPath, resetFilter)

  const addPowerComponentsAction = createNamedAction(sharedPath, addPowerComponents)
  const removePowerComponentsAction = createNamedAction(sharedPath, removePowerComponents)
  const updatePowerComponentsAction = createNamedAction(sharedPath, updatePowerComponents)

  const {
    sourcesSelector,
    configSelectionSelector,
    configSourceSelector,
    togglePopupSelector,
    vendorNameSelector,
    efficiencySelector,
    partNumberSelector,
    partTypeSelector,
    phaseCountSelector,
    voltageConfigsSelector,
    curConfigSelector,
    selectedFilterSelector,
    filterPairsSelector,
    edpPeakSelector,
    edpContinuousSelector,
    outputVoltageSelector,
    labelSelector,
    hintSelector,
    isEditSelector,
    isAddPartSelector
  } = getSelectors(combinedPath, uiPath)

  const {
    powerComponentsSelector,
    powerComponentsTypesSelector
  } = getPowerComponentSelectors(combinedSharedPath)

  const {
    toggleVendorName,
    toggleEfficiency,
    togglePartNumber,
    togglePartType,
    togglePhaseCount,
    initFilters,
    refreshFilters,
  } = getUiActions(uiPath)

  const mapStateToProps = state => {
    return {
      sources: sourcesSelector(state),
      values: voltageConfigsSelector(state),
      powerComponents: powerComponentsSelector(state),
      powerComponentsTypes: powerComponentsTypesSelector(state),
      formElements: elementSelector(state),
      title: labelSelector(state),
      hint: hintSelector(state),
      configSelection: configSelectionSelector(state),
      configSource: configSourceSelector(state),
      togglePopup: togglePopupSelector(state),
      vendorNameFilter: vendorNameSelector(state),
      efficiencyFilter: efficiencySelector(state),
      partNumberFilter: partNumberSelector(state),
      partTypeFilter: partTypeSelector(state),
      phaseCountFilter: phaseCountSelector(state),
      curConfig: curConfigSelector(state),
      selectedFilter: selectedFilterSelector(state),
      filterPairs: filterPairsSelector(state),
      edpPeak: edpPeakSelector(state),
      edpContinuous: edpContinuousSelector(state),
      outputVoltage: outputVoltageSelector(state),
      isEdit: isEditSelector(state),
      isAddPart: isAddPartSelector(state)
    }
  }

  const mapDispatchToProps = dispatch => {
    return {
      onTogglePopup: () => {
        dispatch(togglePopupAction())
      },
      onVendorNameChange: (index, value) => {
        dispatch(toggleVendorName(index, value))
      },
      onEfficiencyChange: (index, value) => {
        dispatch(toggleEfficiency(index, value))
      },
      onPartNumberChange: (index, value) => {
        dispatch(togglePartNumber(index, value))
      },
      onPartTypeChange: (index, value) => {
        dispatch(togglePartType(index, value))
      },
      onPhaseCountChange: (index, value) => {
        dispatch(togglePhaseCount(index, value))
      },
      onUpdateConfig: (value) => {
        dispatch(updateConfigAction(value))
      },
      onUpdateConfigSource: (value) => {
        dispatch(updateConfigSourceAction(value))
      },
      onUpdateCurConfig: (value) => {
        dispatch(updateCurConfigAction(value))
      },
      onAddPart: (value) => {
        dispatch(addConfigAction(value))
      },
      onDeletePart: (value, railType, hasId) => {
        dispatch(deletePartAction(value, railType, hasId))
      },
      setEditPart: (value) => {
        dispatch(setEditPart(value))
      },
      setIsAddPart: (value) => {
        dispatch(setIsAddPart(value))
      },
      onUpdatePhaseCount: (id, railType, value, hasId) => {
        dispatch(updatePhaseCountAction(id, railType, value, hasId))
      },
      onUpdateEfficiency: (id, railType, value, hasId) => {
        dispatch(updateEfficiencyAction(id, railType, value, hasId))
      },
      onUpdateEdpPeak: (value) => {
        dispatch(updateEdpPeakAction(value))
      },
      onUpdateEdpContinuous: (value) => {
        dispatch(updateEdpContinuousAction(value))
      },
      onUpdateOutputVoltage: (value) => {
        dispatch(updateOutputVoltageAction(value))
      },
      onRefreshFilter: () => {
        dispatch(refreshFilters())
      },
      initFilters: (filters) => {
        dispatch(initFilters(filters)())
      },
      onSelectFilter: (filterType) => {
        dispatch(selectFilterAction(filterType))
      },
      onDeleteFilter: (index) => {
        dispatch(deleteFilterAction(index))
      },
      onResetFilter: (defaultFilter) => {
        dispatch(resetFilterAction(defaultFilter))
      },
      onUpdateComponent: (index, type, powerValue, railType) => {
        dispatch(updatePowerComponentsAction(index, type, powerValue, railType))
      },
      onAddComponent: (railType) => {
        dispatch(addPowerComponentsAction(railType))
      },
      onRemoveComponent: (index, railType) => {
        dispatch(removePowerComponentsAction(index, railType))
      },
      onEditPart: (id, value) => {
        dispatch(editConfigAction(id, value))
      }
    }
  }

  return connect(mapStateToProps, mapDispatchToProps)
}

const withRiskConnect = (path, id) => {
  const valuePath = getPath(path, id)
  const runRiskValidation = createNamedAction(valuePath, runRiskValidationAction)

  const mapStateToProps = () => ({})
  const mapDispatchToProps = (dispatch) => {
    return {
      runRiskValidation: () => dispatch(runRiskValidation())
    }
  }

  return connect(mapStateToProps, mapDispatchToProps)
}

const withRisk = (path, id) => R.compose(
  withSagas(path, id),
  withRiskConnect(path, id),
  lifecycle({
    componentDidMount() {
      this.props.runRiskValidation()
    }
  })
)

export { UPDATE_VALUE } from './constants'
export { default as VoltageRegulator } from 'components/form/notebookVoltageRegulator/index'
export {
  withConnect,
  withUiReducer,
  withStateReducer
}

const enhance = R.converge(
  R.compose,
  [
    withStateReducer,
    withUiReducer,
    withConnect,
    withRisk
  ]
)

export default R.curry((path, id) => enhance(path, id)(VoltageRegulator))
